import { type UpdateGeneralUserRequestBody, type User, type UserRole, UserRoles } from '@engagement-web-app/common';
import { Link } from '@tanstack/react-router';
import { EyeIcon, FileText, KeyRound, Mail, Pencil, ShieldOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Avatar } from '@/components/Avatar';
import { DeleteUserDialog } from '@/components/DeleteUserDialog';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { MfaResetDialog } from '@/components/MfaResetDialog';
import { ResetPasswordDialog } from '@/components/ResetPasswordDialog';
import { Skeleton } from '@/components/Skeleton';
import { TextButton } from '@/components/TextButton';
import { type EditUserFormData, UserFormDialog } from '@/components/UserFormDialog';
import { formatPhoneNumber, getFullName } from '@/utils';
import * as styles from './UsersSection.css';

type UsersSectionProps = {
    users: { total: number; list: User[] };
    userRoles: UserRole[];
    isUpdatePending: boolean;
    isDeletePending: boolean;
    isResetPasswordPending: boolean;
    isResetMfaPending: boolean;
    onUpdate: (data: { user: User; body: UpdateGeneralUserRequestBody }) => Promise<User>;
    onDelete: (args: { organizationId: string; uuid: string }) => Promise<void>;
    onResetPassword: (args: { organizationId: string; uuid: string }) => Promise<void>;
    onResetMfa: (args: { organizationId: string; uuid: string }) => Promise<void>;
};

export function UsersSection({
    users,
    userRoles,
    onUpdate,
    onDelete,
    onResetPassword,
    onResetMfa,
    isUpdatePending,
    isDeletePending,
    isResetPasswordPending,
    isResetMfaPending,
}: UsersSectionProps) {
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{
        organizationId: string;
        uuid: string;
        name: string;
    } | null>(null);
    const [resetPasswordTarget, setResetPasswordTarget] = useState<{
        organizationId: string;
        uuid: string;
        name: string;
        email: string;
    } | null>(null);
    const [mfaResetTarget, setMfaResetTarget] = useState<{
        organizationId: string;
        uuid: string;
        name: string;
    } | null>(null);

    const handleEditUser = async (data: EditUserFormData) => {
        if (!editingUser) return;
        await onUpdate({ user: editingUser, body: data });
        setEditingUser(null);
    };

    const handleResetMfa = async () => {
        if (!mfaResetTarget) return;
        await onResetMfa(mfaResetTarget);
        setMfaResetTarget(null);
    };

    const handleDeleteUser = async () => {
        if (!deleteTarget) return;
        await onDelete(deleteTarget);
        setDeleteTarget(null);
    };

    const handleResetPassword = async () => {
        if (!resetPasswordTarget) return;
        await onResetPassword(resetPasswordTarget);
        setResetPasswordTarget(null);
    };

    return (
        <div className={styles.container}>
            {users.list.length === 0 ? (
                // TODO: Visual design for empty state
                <p>ユーザーはまだ登録されていません。</p>
            ) : (
                <Flex as="ul" direction="column" gap="2">
                    {users.list.map((user) => (
                        <Flex
                            as="li"
                            align="center"
                            justify="space-between"
                            key={user.uuid}
                            className={styles.userItem}
                        >
                            <Flex gap="4">
                                <Avatar size="l" mode="client" />
                                <Flex direction="column" gap="2">
                                    <p className={styles.primaryContactName}>{getFullName(user)}</p>
                                    <Flex align="center" gap="2" className={styles.detailRow}>
                                        <Icon icon={Mail} size="sm" className={styles.detailIcon} />
                                        <span className={styles.detailText}>{user.email}</span>
                                    </Flex>
                                    {user.phoneNumber && (
                                        <Flex align="center" gap="2" className={styles.detailRow}>
                                            <Icon icon={FileText} size="sm" className={styles.detailIcon} />
                                            <span className={styles.detailText}>
                                                {formatPhoneNumber(user.phoneNumber)}
                                            </span>
                                        </Flex>
                                    )}
                                    <div className={styles.roleBadge}>{user.role.displayName}</div>
                                </Flex>
                            </Flex>

                            <Flex gap="4">
                                {/* Admins are never narrowed by per-survey permissions. */}
                                {user.role.id === UserRoles.enum.MEMBER && (
                                    <Link
                                        to="/system/organizations/$organizationId/users/$userId/survey-permissions"
                                        params={{ organizationId: user.organizationId, userId: user.uuid }}
                                        className={styles.permissionLink}
                                    >
                                        <Icon icon={EyeIcon} size="sm" />
                                        <span>閲覧権限</span>
                                    </Link>
                                )}

                                <TextButton onClick={() => setEditingUser(user)}>
                                    <Icon icon={Pencil} size="sm" />
                                    <span>編集</span>
                                </TextButton>

                                <TextButton
                                    onClick={() =>
                                        setResetPasswordTarget({
                                            organizationId: user.organizationId,
                                            uuid: user.uuid,
                                            name: getFullName(user),
                                            email: user.email,
                                        })
                                    }
                                >
                                    <Icon icon={KeyRound} size="sm" />
                                    <span>パスワードリセット</span>
                                </TextButton>

                                <TextButton
                                    onClick={() =>
                                        setMfaResetTarget({
                                            organizationId: user.organizationId,
                                            uuid: user.uuid,
                                            name: getFullName(user),
                                        })
                                    }
                                >
                                    <Icon icon={ShieldOff} size="sm" />
                                    <span>MFAリセット</span>
                                </TextButton>

                                <TextButton
                                    variant="danger"
                                    onClick={() =>
                                        setDeleteTarget({
                                            uuid: user.uuid,
                                            organizationId: user.organizationId,
                                            name: getFullName(user),
                                        })
                                    }
                                >
                                    <Icon icon={Trash2} size="sm" />
                                    <span>削除</span>
                                </TextButton>
                            </Flex>
                        </Flex>
                    ))}
                </Flex>
            )}

            <DeleteUserDialog
                target={deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                isSubmitting={isDeletePending}
                onSubmit={handleDeleteUser}
            />

            <ResetPasswordDialog
                target={resetPasswordTarget}
                onOpenChange={(open) => !open && setResetPasswordTarget(null)}
                isSubmitting={isResetPasswordPending}
                onSubmit={handleResetPassword}
            />

            <MfaResetDialog
                target={mfaResetTarget}
                onOpenChange={(open) => !open && setMfaResetTarget(null)}
                isSubmitting={isResetMfaPending}
                onSubmit={handleResetMfa}
            />

            <UserFormDialog
                mode="edit"
                userRoles={userRoles}
                user={editingUser}
                isOpen={editingUser !== null}
                onOpenChange={(open) => !open && setEditingUser(null)}
                isSubmitting={isUpdatePending}
                onSubmit={handleEditUser}
            />
        </div>
    );
}

const SKELETON_ROW_COUNT = 3;

export function UsersSectionSkeleton() {
    return (
        <div className={styles.container}>
            <Flex direction="column" gap="2">
                {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, rows have no identity
                    <Flex key={i} align="center" justify="space-between" className={styles.userItem}>
                        <Flex gap="4">
                            <Skeleton.Circle radius={64} />
                            <Flex direction="column" gap="2">
                                <Skeleton.Line width={140} height="lg" />
                                <Skeleton.Line width={180} height="sm" />
                                <Skeleton.Box width={70} height={28} />
                            </Flex>
                        </Flex>

                        <Flex gap="4">
                            <Skeleton.Line width={50} height="sm" />
                            <Skeleton.Line width={120} height="sm" />
                            <Skeleton.Line width={90} height="sm" />
                            <Skeleton.Line width={50} height="sm" />
                        </Flex>
                    </Flex>
                ))}
            </Flex>
        </div>
    );
}
